'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { flash } from 'DesignData/actions/Flash';
import { SET_CURSOR_IN_IDE_CODE_MIRROR } from 'DesignData/enums/FlashKeys';
import * as colors from 'HubStyleTokens/colors';
import UIMedia from 'UIComponents/layout/UIMedia';
import UIMediaLeft from 'UIComponents/layout/UIMediaLeft';
import UIMediaBody from 'UIComponents/layout/UIMediaBody';
import UIIconCircle from 'UIComponents/icon/UIIconCircle';
import UILink from 'UIComponents/link/UILink';
import UIButton from 'UIComponents/button/UIButton';
import EditorError from 'DesignData/models/validation/EditorError';
import CodeEditorError from 'DesignData/models/validation/CodeEditorError';
import { ERROR_MESSAGE_COMPONENTS } from './ErrorMessageComponent';
import { ERROR_CONSOLE_COLORS } from 'DesignData/constants/Colors';
import { EDITOR_ERROR_ICON_MAP } from 'DesignData/constants/Icons';
import SanitizedText from 'sanitize-text/components/SanitizedText';

const i18nErrorTypeKey = key => `DesignEditorsUI.statusBar.errorTypes.${key}`;

const mapStateToProps = null;
const mapDispatchToProps = {
  flash
};

class ErrorMessage extends PureComponent {
  constructor(...args) {
    super(...args);

    this.onCodeEditorErrorClick = () => {
      const {
        error
      } = this.props;
      const {
        lineNumber,
        column,
        endLineNumber,
        endColumn,
        sessionId
      } = error;

      if (!(sessionId && error instanceof CodeEditorError)) {
        return;
      }

      this.props.flash(SET_CURSOR_IN_IDE_CODE_MIRROR, {
        sessionId,
        lineNumber,
        endLineNumber,
        column,
        endColumn
      });
    };
  }

  renderTypeAndNumber() {
    const {
      error: {
        type
      },
      errorNumber
    } = this.props;
    const icon = EDITOR_ERROR_ICON_MAP[type];
    return /*#__PURE__*/_jsxs(UIMediaLeft, {
      children: [/*#__PURE__*/_jsx(UIIconCircle, {
        name: icon,
        color: colors.OLAF,
        backgroundColor: ERROR_CONSOLE_COLORS[type],
        size: 12,
        padding: 0.2
      }), /*#__PURE__*/_jsx("span", {
        className: "p-left-2",
        children: `${errorNumber}.`
      })]
    });
  }

  renderMessageLink(message, messageLink, messageLinkExternal, onMessageClick) {
    return /*#__PURE__*/_jsx(UILink, {
      external: messageLinkExternal,
      onClick: onMessageClick,
      href: messageLink,
      children: message
    });
  }

  renderMessagePlain(message) {
    return /*#__PURE__*/_jsx("span", {
      children: message
    });
  }

  renderMessage() {
    const {
      error: {
        message,
        messageLink,
        messageLinkExternal,
        onMessageClick
      }
    } = this.props;
    const renderedMessage = messageLink || onMessageClick ? this.renderMessageLink(message, messageLink, messageLinkExternal, onMessageClick) : this.renderMessagePlain(message);
    return this.renderMessageWrapper(renderedMessage);
  }

  renderMessageWrapper(children) {
    return /*#__PURE__*/_jsxs("span", {
      children: [/*#__PURE__*/_jsx("span", {
        className: "p-right-2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nErrorTypeKey(this.props.error.type)
        })
      }), children]
    });
  }

  renderHelpMessage() {
    const {
      error: {
        helpMessage,
        helpMessageLink,
        helpMessageLinkExternal,
        onHelpMessageClick
      }
    } = this.props;
    if (!helpMessage) return null;
    const renderedMessage = helpMessageLink || onHelpMessageClick ? this.renderMessageLink(helpMessage, helpMessageLink, helpMessageLinkExternal, onHelpMessageClick) : this.renderMessagePlain(helpMessage);
    return /*#__PURE__*/_jsx("div", {
      children: renderedMessage
    });
  }

  renderEditorError() {
    const {
      errorMessageComponent
    } = this.props.error;

    if (ERROR_MESSAGE_COMPONENTS[errorMessageComponent]) {
      const ErrorMessageComponent = ERROR_MESSAGE_COMPONENTS[errorMessageComponent];
      return this.renderMessageWrapper([/*#__PURE__*/_jsx(ErrorMessageComponent, {
        error: this.props.error
      }, errorMessageComponent)]);
    } else {
      const renderedMessage = this.renderMessage();
      const renderedHelpMessage = this.renderHelpMessage();
      return /*#__PURE__*/_jsxs("span", {
        children: [renderedMessage, renderedHelpMessage]
      });
    }
  }

  renderCodeEditorError() {
    const {
      lineNumber,
      column,
      message,
      rawMessage
    } = this.props.error;
    return this.renderMessageWrapper([/*#__PURE__*/_jsx(UIButton, {
      use: "link",
      onClick: this.onCodeEditorErrorClick,
      className: "code-line-column-link",
      children: /*#__PURE__*/_jsxs("code", {
        children: [lineNumber + 1, ":", column]
      })
    }, "line-column"), /*#__PURE__*/_jsx(SanitizedText, {
      className: "code-error-message",
      body: message || rawMessage,
      WrapperComponent: "code"
    }, "message")]);
  }

  renderBody() {
    if (this.props.error instanceof CodeEditorError) {
      return this.renderCodeEditorError();
    }

    return this.renderEditorError();
  }

  render() {
    const renderedTypeAndNumber = this.renderTypeAndNumber();
    const body = this.renderBody();
    return /*#__PURE__*/_jsxs(UIMedia, {
      className: "error-message",
      style: this.props.style,
      children: [renderedTypeAndNumber, /*#__PURE__*/_jsx(UIMediaBody, {
        children: body
      })]
    });
  }

}

ErrorMessage.propTypes = {
  error: EditorError.editorErrorPropType.isRequired,
  errorNumber: PropTypes.number.isRequired,
  flash: PropTypes.func.isRequired,
  style: PropTypes.object
};
export default connect(mapStateToProps, mapDispatchToProps)(ErrorMessage);